/**
 * Datenmodell der Frametabelle: Trace-Ring und Static-Zeilen.
 * Portierung von traceRing_/staticRows_/staticRowOrder_ samt
 * AppendFrameTrace() und AppendFrameStatic() aus CanMonitorApp.cpp.
 *
 * Abweichung vom Original (bewusst, wegen der AP-5-Abnahmekriterien):
 * Das C++ legt je Zeile 14 fertig formatierte std::string ab. Bei 100.000
 * Zeilen und 5.000 Frames/s wären das in JS 1,4 Mio. Strings und ein
 * Allokationsgewitter pro Sekunde. Hier werden stattdessen die Rohwerte
 * spaltenweise in TypedArrays gehalten und erst beim Zeichnen formatiert —
 * für die rund 50 sichtbaren Zeilen statt für alle. Die sichtbaren
 * Spaltentexte sind identisch (siehe format.ts).
 */

import type { ViewMode } from '../../app/settings';
import type { DecodedInfo, UnpackedFrame } from '../../worker/protocol';
import { COL, TABLE_COLUMN_COUNT } from './columns';
import { formatCanIdDisplay, formatMillis, hexByte } from './format';
import { nextUpdateTick, type RowState } from './colors';

/** traceRing_.size() >= 2000 -> pop_front() im Original. */
export const DEFAULT_TRACE_CAPACITY = 2000;

/** Kennung samt Nutzdaten der angeklickten Zeile. */
export interface RowKey {
  seq: number;
  id: number;
  dlc: number;
  data: Uint8Array;
}

const EMPTY_ROW_KEY: RowKey = { seq: 0, id: 0, dlc: 0, data: new Uint8Array(0) };

/** Lesezugriff, den der Renderer braucht — mehr sieht er vom Modell nicht. */
export interface TableSource {
  rowCount(): number;
  cell(row: number, col: number): string;
  rowState(row: number, out: RowState): RowState;
  /** Für den Doppelklick (`frame:activated`). */
  rowKey(row: number): RowKey;
  /**
   * Zeilenkennung, die eine Zeile über Modelländerungen hinweg wiederfindet —
   * nur dafür da, aufgeklappte Signallisten an ihrer Botschaft zu halten.
   */
  rowIdentity(row: number): number;
}

interface StaticRow {
  id: number;
  count: number;
  lastSeq: number;
  lastDlc: number;
  data: Uint8Array;
  cycleMs: number;
  lastTimestampS: number;
  name: string;
  description: string;
  known: boolean;
  isError: boolean;
  lastUpdateMs: number;
}

export interface FrameTableOptions {
  /** Zeilen im Trace-Ring; Vorgabe entspricht dem Original. */
  traceCapacity?: number;
  viewMode?: ViewMode;
}

export class FrameTable implements TableSource {
  private mode: ViewMode;
  private readonly capacity: number;

  // --- Trace-Ring, spaltenweise ---
  private readonly seqCol: Uint32Array;
  private readonly idCol: Uint32Array;
  private readonly dlcCol: Uint8Array;
  private readonly flagsCol: Uint8Array;
  private readonly dataCol: Uint8Array;
  private readonly dtCol: Float32Array;
  private readonly updateCol: Float64Array;
  private readonly errorCol: Uint8Array;
  private readonly nameCol: string[];
  private readonly descCol: string[];
  private head = 0;
  private length = 0;

  // --- Static-Zeilen ---
  private readonly staticRows = new Map<number, StaticRow>();
  private readonly staticOrder: number[] = [];

  /** lastFrameTimestamp_s_/hasLastFrameTime_: dt(ms) im Trace ist global, nicht je ID. */
  private lastTimestampS = 0;
  private hasLastTimestamp = false;

  constructor(options: FrameTableOptions = {}) {
    this.capacity = Math.max(1, options.traceCapacity ?? DEFAULT_TRACE_CAPACITY);
    this.mode = options.viewMode ?? 'static';
    const cap = this.capacity;
    this.seqCol = new Uint32Array(cap);
    this.idCol = new Uint32Array(cap);
    this.dlcCol = new Uint8Array(cap);
    this.flagsCol = new Uint8Array(cap);
    this.dataCol = new Uint8Array(cap * 8);
    this.dtCol = new Float32Array(cap);
    this.updateCol = new Float64Array(cap);
    this.errorCol = new Uint8Array(cap);
    this.nameCol = new Array<string>(cap).fill('');
    this.descCol = new Array<string>(cap).fill('');
  }

  get viewMode(): ViewMode {
    return this.mode;
  }

  /**
   * SetViewMode(): schaltet nur um, welches Modell angezeigt wird. Beide werden
   * dauerhaft gefuellt, damit der Wechsel keine empfangenen Frames verwirft.
   */
  setViewMode(mode: ViewMode): void {
    this.mode = mode;
  }

  /** ClearTable(): beide Speicher, Zykluszeit-Basis und Ring zurücksetzen. */
  clear(): void {
    this.head = 0;
    this.length = 0;
    this.staticRows.clear();
    this.staticOrder.length = 0;
    this.hasLastTimestamp = false;
    this.lastTimestampS = 0;
  }

  /**
   * Ein durchgelassener Frame aus einem `rx-batch`. `frame.data` darf in den
   * Batch-Puffer zeigen — es wird sofort kopiert.
   */
  append(frame: UnpackedFrame, decoded: DecodedInfo | null, nowMs: number): void {
    this.appendTrace(frame, decoded, nowMs);
    this.appendStatic(frame, decoded, nowMs);
  }

  private appendTrace(frame: UnpackedFrame, decoded: DecodedInfo | null, nowMs: number): void {
    let dtMs = 0;
    if (this.hasLastTimestamp) {
      dtMs = (frame.timestamp_s - this.lastTimestampS) * 1000;
      if (dtMs < 0) dtMs = 0; // Uhrsprung beim Reconnect
    }
    this.hasLastTimestamp = true;
    this.lastTimestampS = frame.timestamp_s;

    const slot = (this.head + this.length) % this.capacity;
    if (this.length === this.capacity) this.head = (this.head + 1) % this.capacity;
    else this.length += 1;

    const name = decoded?.name ?? '';
    this.seqCol[slot] = frame.seq;
    this.idCol[slot] = frame.id;
    this.dlcCol[slot] = frame.dlc;
    this.flagsCol[slot] = frame.flags;
    this.dtCol[slot] = dtMs;
    this.nameCol[slot] = name;
    this.descCol[slot] = decoded?.description ?? '';
    const isError = decoded?.isError === true;
    this.errorCol[slot] = isError ? 1 : 0;
    // Trace-Zeilen sind immer neu, deshalb kein vorheriger Tick zum Drosseln.
    this.updateCol[slot] = nextUpdateTick(0, name !== '', isError, nowMs);
    this.dataCol.set(frame.data, slot * 8);
  }

  private appendStatic(frame: UnpackedFrame, decoded: DecodedInfo | null, nowMs: number): void {
    const name = decoded?.name ?? '';
    const description = decoded?.description ?? '';
    const isError = decoded?.isError === true;
    const known = name !== '';

    const existing = this.staticRows.get(frame.id);
    if (existing === undefined) {
      const data = new Uint8Array(8);
      data.set(frame.data);
      this.staticRows.set(frame.id, {
        id: frame.id,
        count: 1,
        lastSeq: frame.seq,
        lastDlc: frame.dlc,
        data,
        cycleMs: 0,
        lastTimestampS: frame.timestamp_s,
        name,
        description,
        known,
        isError,
        lastUpdateMs: nextUpdateTick(0, known, isError, nowMs),
      });
      this.staticOrder.push(frame.id);
      return;
    }

    existing.count += 1;
    const rawCycleMs = (frame.timestamp_s - existing.lastTimestampS) * 1000;
    existing.cycleMs = rawCycleMs > 0 ? rawCycleMs : 0;
    existing.lastTimestampS = frame.timestamp_s;
    existing.lastSeq = frame.seq;
    existing.lastDlc = frame.dlc;
    existing.data.set(frame.data);
    existing.name = name;
    existing.description = description;
    existing.known = known;
    existing.isError = isError;
    existing.lastUpdateMs = nextUpdateTick(existing.lastUpdateMs, known, isError, nowMs);
  }

  // -------------------------------------------------------------------------
  // TableSource
  // -------------------------------------------------------------------------

  rowCount(): number {
    return this.mode === 'trace' ? this.length : this.staticOrder.length;
  }

  cell(row: number, col: number): string {
    if (col < 0 || col >= TABLE_COLUMN_COUNT) return '';
    return this.mode === 'trace' ? this.traceCell(row, col) : this.staticCell(row, col);
  }

  private traceCell(row: number, col: number): string {
    if (row < 0 || row >= this.length) return '';
    const slot = (this.head + row) % this.capacity;
    switch (col) {
      case COL.seq:
        return String(this.seqCol[slot]);
      case COL.id:
        return formatCanIdDisplay(this.idCol[slot]!);
      case COL.dlc:
        return String(this.dlcCol[slot]);
      case COL.name:
        return this.nameCol[slot] ?? '';
      case COL.description:
        return this.descCol[slot] ?? '';
      case COL.dt:
        return formatMillis(this.dtCol[slot]!);
      default: {
        const byte = col - COL.firstByte;
        // Bytes ab dlc bleiben leer, nicht "00" — wie im Original.
        if (byte >= this.dlcCol[slot]!) return '';
        return hexByte(this.dataCol[slot * 8 + byte]!);
      }
    }
  }

  private staticCell(row: number, col: number): string {
    const entry = this.staticAt(row);
    if (entry === undefined) return '';
    switch (col) {
      case COL.seq:
        return String(entry.count);
      case COL.id:
        return formatCanIdDisplay(entry.id);
      case COL.dlc:
        return String(entry.lastDlc);
      case COL.name:
        return entry.name;
      case COL.description:
        return entry.description;
      case COL.dt:
        return formatMillis(entry.cycleMs);
      default: {
        const byte = col - COL.firstByte;
        if (byte >= entry.lastDlc) return '';
        return hexByte(entry.data[byte]!);
      }
    }
  }

  /** Schreibt in `out` statt zu allokieren — wird je sichtbarer Zeile aufgerufen. */
  rowState(row: number, out: RowState): RowState {
    if (this.mode === 'trace') {
      if (row < 0 || row >= this.length) {
        out.known = false;
        out.isError = false;
        out.lastUpdateMs = 0;
        return out;
      }
      const slot = (this.head + row) % this.capacity;
      out.known = (this.nameCol[slot] ?? '') !== '';
      out.isError = this.errorCol[slot] === 1;
      out.lastUpdateMs = this.updateCol[slot]!;
      return out;
    }
    const entry = this.staticAt(row);
    out.known = entry?.known ?? false;
    out.isError = entry?.isError ?? false;
    out.lastUpdateMs = entry?.lastUpdateMs ?? 0;
    return out;
  }

  rowKey(row: number): RowKey {
    if (this.mode === 'trace') {
      if (row < 0 || row >= this.length) return EMPTY_ROW_KEY;
      const slot = (this.head + row) % this.capacity;
      const dlc = this.dlcCol[slot]!;
      return {
        seq: this.seqCol[slot]!,
        id: this.idCol[slot]!,
        dlc,
        data: this.dataCol.slice(slot * 8, slot * 8 + Math.min(dlc, 8)),
      };
    }
    const entry = this.staticAt(row);
    if (entry === undefined) return EMPTY_ROW_KEY;
    return {
      seq: entry.lastSeq,
      id: entry.id,
      dlc: entry.lastDlc,
      data: entry.data.slice(0, Math.min(entry.lastDlc, 8)),
    };
  }

  private staticAt(row: number): StaticRow | undefined {
    const id = this.staticOrder[row];
    return id === undefined ? undefined : this.staticRows.get(id);
  }

  /**
   * Im Trace-Modus wandert eine Zeile mit jedem verdrängten Frame nach oben,
   * ihre laufende Nummer bleibt aber eindeutig; im Static-Modus ist es die ID.
   */
  rowIdentity(row: number): number {
    if (this.mode === 'trace') {
      if (row < 0 || row >= this.length) return -1;
      return this.seqCol[(this.head + row) % this.capacity]!;
    }
    return this.staticOrder[row] ?? -1;
  }
}
